import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isNil, get } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Avatar from '../Avatar';
import { secToTimestamp, collaborationEventBus } from '../../../Utils';
import CommentBox, { COMMENT_BOX_EVENT_NAME } from '../CommentsBox';
import CollaborationCommetShape from '../../PropsShape/CollaborationCommentShape';
import { MARKER_CLICK_EVENT } from '../../../MediaViewer/consts';
import reportToBigQuery from '../../../Utils/bigQueryReporter';
import { sendMixPanelEvent } from '../../../Utils/mixpanel';
import { SELECT_COMMENT, EDIT_OR_DELETE_COMMENT } from '../../../Utils/mixpanel/mixpanelEvents';
import Time from '../Time';
import { COLLABORATION_ACTIVE_STATUS } from '../../stateManager/collaborations/types';
import * as CommentsActions from '../../actions/comments';
import { commentEventsBus, COMMENT_CLICK_EVENT, REPLY_CLICKED } from '../../eventsBus/comment';
import { isCreateCommentsLoading } from '../../selectors/loading';
import * as CurrentsSelectors from '../../selectors/current';
import * as CollaboratorsSelectors from '../../selectors/collaborators';
import * as CommentsSelectors from '../../selectors/comments';
import * as CurrentsActions from '../../actions/current';
import * as FilesSelectors from '../../selectors/files';
import * as Translations from '../../providers/i18n';
import ReplyIcon from './ReplyIcon';
import './style.scss';

class Comment extends React.Component {
    constructor(props) {
        super(props);

        this.state = { isReplyClicked: false, isEditMode: false, deleting: false };

        this.setReplyClicked = this.setReplyClicked.bind(this);
        this.setDelete = this.setDelete.bind(this);
        this.showTimeCode = this.showTimeCode.bind(this);
        this.isPending = this.isPending.bind(this);
        this.selected = this.selected.bind(this);
        this.startEdit = this.startEdit.bind(this);
        this.stopEdit = this.stopEdit.bind(this);
        this.showEditOptions = this.showEditOptions.bind(this);
        this.isNotEditMode = this.isNotEditMode.bind(this);
        this.markerSelected = this.markerSelected.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.shouldShowReplyButton = this.shouldShowReplyButton.bind(this);
        this.hasAnnotations = this.hasAnnotations.bind(this);
        this.getCurrentUsername = this.getCurrentUsername.bind(this);
    }

    componentDidMount() {
        collaborationEventBus.on(MARKER_CLICK_EVENT, this.markerSelected);
    }

    emitCommentClicked({ timecode }) {
        const { comment } = this.props;
        commentEventsBus.emit(COMMENT_CLICK_EVENT, { timecode, commentId: comment.id });
    }

    hasAnnotations() {
        const { comment } = this.props;

        return !!get(comment, 'annotations.length');
    }

    setReplyClicked(e) {
        const {
            isCommentSelected
        } = this.props;

        e && isCommentSelected && e.stopPropagation();

        this.setState({
            isReplyClicked: !this.state.isReplyClicked
        });

        if (!this.state.isReplyClicked) {
            commentEventsBus.emit(REPLY_CLICKED, this.props.comment.id);
        }
    }

    startEdit(e) {
        e.stopPropagation();

        this.setState({
            isEditMode: true
        });
    }

    stopEdit(body) {
        if (body) {
            const {
                updateComment,
                comment,
                isReply
            } = this.props;

            if (!comment.pending) { return; }

            updateComment({ id: comment.id, body });

            reportToBigQuery({
                group: 'collaboration',
                type: 'edit_comment_in_collaboration_conversation',
                reason: isReply ? 'reply' : 'comment'
            });

            sendMixPanelEvent({
                eventName: EDIT_OR_DELETE_COMMENT,
                eventData: {
                    'Action': 'edit',
                    'Has Annotations': !!comment.annotations.length
                }
            });
        }

        this.setState({
            isEditMode: false
        });
    }

    setCommentHeight() {
        this.comment.style.height = `${this.comment.getBoundingClientRect().height}px`;
    }

    setDelete() {
        const { comment } = this.props;

        if (!comment.pending) {return;}

        this.setCommentHeight();

        this.setState({
            deleting: true
        });
    }

    handleDelete() {
        const {
            comment,
            setCurrentCommentId,
            deleteComment,
            isReply
        } = this.props;

        if (this.state.deleting) {
            deleteComment(comment.id);
            setCurrentCommentId(undefined);

            reportToBigQuery({
                group: 'collaboration',
                type: 'delete_comment_in_collaboration_conversation',
                reason: isReply ? 'reply' : 'comment'
            });

            sendMixPanelEvent({
                eventName: EDIT_OR_DELETE_COMMENT,
                eventData: {
                    'Action': 'delete',
                    'Has Annotations': !!comment.annotations.length
                }
            });
        }
    }

    showTimeCode() {
        const { comment } = this.props;
        return !isNil(comment.timecode) && this.isNotEditMode();
    }

    showEditOptions() {
        return this.isPending() && this.isNotEditMode();
    }

    isPending() {
        return this.props.comment.pending;
    }

    isNotEditMode() {
        return !this.state.isEditMode;
    }

    markerSelected({ id }) {
        this.props.setCurrentCommentId(id);
    }

    selected(e) {
        const {
            comment,
            setCurrentCommentId,
            isReply,
            isCommentSelected,
            parentComment,
            setCurrentFileId,
            setCurrentFilerrId,
            filerrId
        } = this.props;

        if (isCommentSelected && e.detail === COMMENT_BOX_EVENT_NAME) {
            return;
        }

        sendMixPanelEvent({
            eventName: SELECT_COMMENT,
            eventData: {
                'Opened Comment Source': 'Comments Panel'
            }
        });

        isReply ? this.emitCommentClicked(parentComment) : this.emitCommentClicked(comment);

        if (isCommentSelected) {
            setCurrentCommentId(undefined);
        } else {
            const id = isReply ? comment.parentId : comment.id;
            setCurrentCommentId(id);
            setCurrentFileId(comment.fileId);
            setCurrentFilerrId(filerrId);
        }
    }

    shouldShowReplyButton() {
        const {
            hasReplies,
            isRevisionRequest
        } = this.props;
        const { isReplyClicked } = this.state;

        if (isRevisionRequest) { return false; }

        return !isReplyClicked && !hasReplies;
    }

    getCurrentUsername() {
        const {
            standalone,
            username,
            currentCollaboratorId,
            comment,
            fullName,
            i18n
        } = this.props;

        if (currentCollaboratorId === comment.collaboratorId) {
            return i18n.t('attachments_preview.general.me');
        }

        if (standalone && fullName) {
            return fullName;
        }

        return username;
    }

    render() {
        const {
            username,
            userImage,
            comment,
            isReply,
            currentCollaboratorId,
            isCommentSelected,
            disabled,
            removed,
            i18n
        } = this.props;

        const {
            createdAt,
            timecode,
            body,
            id,
            collaboratorId
        } = comment;

        const { isReplyClicked, deleting } = this.state;

        const timeStamp = secToTimestamp(timecode);

        const classesInerr = classNames('inner', { reply: isReply });

        const classesWrapper = classNames('comment-wrapper', {
            selected: isCommentSelected,
            'delete-animation': deleting,
            'others-comment': collaboratorId !== currentCollaboratorId,
            removed
        });

        const parentId = isReply ? comment.parentId : id;

        const currentUsername = this.getCurrentUsername();

        if (!currentUsername) { return null; }

        return (
            <div className={classesWrapper}
                onClick={this.selected} onAnimationEnd={this.handleDelete}
                id={`comment-${id}`}
                ref={(ref) => this.comment = ref}>
                <div className={classesInerr}>
                    <Avatar username={username} src={userImage}/>
                    <div className="main-comment">
                        <div className="comment-header">
                            <h3>{currentUsername}</h3>
                            {!this.isPending() && <span className="date"><Time dateTime={createdAt} type="RELATIVE"/></span>}
                            {this.isPending() && <span className="pending">{i18n.t('attachments_preview.collaboration.comments.pending')}</span>}
                        </div>
                        {this.isNotEditMode() && <div className="comment-body">
                            <div className="comment-content">{body}</div>
                            {this.shouldShowReplyButton() &&
                                <button className="reply-button" onClick={this.setReplyClicked} disabled={disabled}>
                                    <ReplyIcon/>
                                    <span>{i18n.t('attachments_preview.collaboration.comments.reply')}</span>
                                </button>
                            }
                        </div>}
                    </div>
                    <div className="comment-stamp">
                        {this.showTimeCode() && <div className="time">{timeStamp}</div>}
                        {this.hasAnnotations() && <i className="annotation" style={{ backgroundColor: comment.annotations.length && comment.annotations[0].target ? comment.annotations[0].target.selector.strokeColor : '#FF62AD' }}></i>}
                    </div>
                    {this.showEditOptions() && <button className="delete" onClick={this.setDelete}></button>}
                    {this.showEditOptions() && <button className="edit" onClick={this.startEdit}></button>}
                </div>
                {isReplyClicked && <CommentBox parentId={parentId} defaultOpen={true} setReplyClicked={this.setReplyClicked} isReply={isReply}/>}
                {!this.isNotEditMode() && <CommentBox defaultOpen={true} stopEdit={this.stopEdit} body={body} timecodeEdit={timecode}/>}
            </div>
        );
    }

}

Comment.propTypes = {
    username: PropTypes.string.isRequired,
    userImage: PropTypes.string,
    comment: CollaborationCommetShape.isRequired,
    hasReplies: PropTypes.bool,
    isReply: PropTypes.bool,
    isCommentSelected: PropTypes.bool,
    setCurrentCommentId: PropTypes.func,
    currentCollaboratorId: PropTypes.string,
    parentComment: CollaborationCommetShape,
    disabled: PropTypes.bool,
    standalone: PropTypes.bool,
    fullName: PropTypes.string,
    removed: PropTypes.bool,
    deleteComment: PropTypes.func,
    updateComment: PropTypes.func,
    setCurrentFileId: PropTypes.func,
    setCurrentFilerrId: PropTypes.func,
    filerrId: PropTypes.string,
    isRevisionRequest: PropTypes.bool,
    i18n: PropTypes.shape({
        t: PropTypes.func
    })
};

Comment.defaultProps = {
    userImage: '',
    standalone: false,
    fullName: '',
    removed: false,
    updateComment: () => null,
    deleteComment: () => null,
    setCurrentFileId: () => null,
    setCurrentFilerrId: () => null,
    filerrId: ''
};

const mapStateToProps = (state, ownProps) => {
    const comment = CommentsSelectors.getCommentById(state, ownProps.commentId);
    const { collaboratorId, id, parentId, fileId } = comment;
    const { userImage, fullName, removed } = CollaboratorsSelectors.getCollaboratorById(state, collaboratorId);
    const username = CollaboratorsSelectors.getCollaboratorSafeUsernameById(state, collaboratorId);
    const currentCollaboratorId = CurrentsSelectors.getCurrentCollaboratorId(state);
    const parentComment = CommentsSelectors.getCommentById(state, parentId);
    const isSubmittingReviews = isCreateCommentsLoading(state);
    const collaborationStatus = FilesSelectors.getCurrentFileStatus(state);
    const filerrId = FilesSelectors.getFilerrIdByFileId(state, fileId);

    return {
        username,
        userImage,
        currentCollaboratorId,
        hasReplies: CommentsSelectors.hasRepliesByCommentId(state, id),
        isCommentSelected: CommentsSelectors.isCommentSelected(state, id, parentId),
        parentComment,
        disabled: isSubmittingReviews || collaborationStatus !== COLLABORATION_ACTIVE_STATUS,
        standalone: state.settings.standalone,
        fullName,
        removed,
        comment,
        filerrId
    };
};

export default compose(
    Translations.connect,
    connect(mapStateToProps, {
        setCurrentCommentId: CurrentsActions.setCurrentCommentId,
        deleteComment: CommentsActions.deleteComment,
        updateComment: CommentsActions.updateComment,
        setCurrentFileId: CurrentsActions.setCurrentFileId,
        setCurrentFilerrId: CurrentsActions.setCurrentFilerrId
    }))(Comment);
